import React, { useCallback, Fragment, useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Modal from '@mui/material/Modal';
import { useTheme } from '@mui/material';
import { LakeStatusChart } from '../Components/Charts';
import axios from "axios";
import mapboxgl from "mapbox-gl";
import 'mapbox-gl/dist/mapbox-gl.css';
import wellknown from "wellknown"; // Library to convert WKT to GeoJSON
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { TextField } from '@mui/material';
import { getAuth } from 'firebase/auth';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import BASE_URL from '../config';
import { useOrg } from "../contexts/OrgContext";
import '../styles/index.css';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { toast } from 'react-toastify';
import ShowButton from "../Components/UI/Button";
import { usePermissions } from "../contexts/PermissionContext";
import { FaSpinner } from "react-icons/fa";
import UnifiedTaskModal from "../Components/Task/UnifiedTaskModal";
import CreateTaskDialog from '../Components/Task/CreateTaskDialog';
import SiteOpsCard from '../Components/Task/SiteOpsCard';
import Backdrop from '@mui/material/Backdrop';
import {
  getTreeDetailsParentSx,
  getTreeHeadingDivSx,
  getTreesTextSx,
  getStatCardEcologicalSx,
  getStatIconWrapperSx,
  getStatInfoDivSx,
  getStatLabelSx,
  getStatValueSx
} from '../Components/ecological/EcologicalMapStyles';
import SentinelAnalysisPanel from '../Components/ecological/SentinelAnalysisPanel';
import CircularProgress from '@mui/material/CircularProgress';



mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;


function Lake() {
  const theme = useTheme();
  const { can } = usePermissions();
  const { selectedOrgId } = useOrg();
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedLakeId, setSelectedLakeId] = useState('');
  const [siteCode, setSiteCode] = useState('');
  const dueDateRef = useRef();
  const [open, setOpen] = React.useState(false);
  const [sitePolygon, setSitePolygon] = useState([]);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalAreaAcre: 0,
    totalWaterAcre: 0,
    infestedPercentage: 0,
  });
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [lakeOptions, setLakeOptions] = useState([]);
  const [selectedLake, setSelectedLake] = useState('');
  const [lakeCode, setLakeCode] = useState('');
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapCenter, setMapCenter] = useState([76.95390347, 11.01615852]); // Default center [lng, lat]
  const satLayerSeqRef = useRef(0);
  const statsSeqRef = useRef(0);
  const siterunsSeqRef = useRef(0);
  const sceneImagesSeqRef = useRef(0);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(true); // Loading state for work orders

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Timestamp selection states
  const [availableTimestamps, setAvailableTimestamps] = useState([]);
  const [selectedTimestamp, setSelectedTimestamp] = useState('');
  const [selectedRunId, setSelectedRunId] = useState('');
  const [loadingTimestamps, setLoadingTimestamps] = useState(false);

  // Vegetation (lake_vegetation MVT) + satellite RGB image overlay controls
  const [vegVisible, setVegVisible] = useState(true);
  const [vegOpacity, setVegOpacity] = useState(0.6);
  const [imageVisible, setImageVisible] = useState(true);
  const [imageOpacity, setImageOpacity] = useState(1);
  const [sceneImages, setSceneImages] = useState([]); // scene_images rows for the selected site (kind=rgb)

  const [sites, setSites] = useState([]); // Stores fetched sites
  const [selectedSite, setSelectedSite] = useState(""); // Selected site
  const [selectedSiteCode, setSelectedSiteCode] = useState(""); // Store site code
  const [isPanelMinimized, setIsPanelMinimized] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [siteOptions, setsiteOptions] = useState([]);

  const currentSite = {
    name: selectedSite,
    id: selectedSiteId,
    code: siteCode
  };

  // 🔹 Task Modal states
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);

  const handleRowClick = async (item, event, type = "task") => {
    console.log("Row clicked:", item, type);
    setSelectedTaskId(item.id);
    setIsTaskModalOpen(true);
  };



  const handleCloseModal = () => {
    setIsTaskModalOpen(false);
    setSelectedTaskId(null);
  };

  useEffect(() => {
    // Fetch only lakes from backend
    const fetchLakeOptions = async () => {
      try {
        const user = getAuth().currentUser;
        const token = user && await user.getIdToken();
        const res = await fetch(`${BASE_URL}/api/lake-sites`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLakeOptions(await res.json());
      } catch (err) {
        console.error('Failed to fetch lake options:', err);
      }
    };
    fetchLakeOptions();
  }, []);

  useEffect(() => {
    if (selectedSiteCode) {
      // Reset selected run when site changes
      setSelectedRunId(''); // ✅ Reset run_id
      setSelectedTimestamp(''); // ✅ Optional if you want to clear timestamp too

      fetchSiteruns(selectedSiteCode);
      fetchSceneImages(selectedSiteCode);
    }
  }, [selectedSiteCode]);

  // Fetch the tool-agnostic scene catalog (downloaded RGB rasters) for the site,
  // used to find the s3_uri matching the currently selected run's date.
  const fetchSceneImages = async (siteCode) => {
    if (!siteCode) return;
    const seq = ++sceneImagesSeqRef.current;
    try {
      const user = getAuth().currentUser;
      const token = user && await user.getIdToken();
      const response = await fetch(`${BASE_URL}/api/scene-images/${siteCode}?kind=rgb`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (seq !== sceneImagesSeqRef.current) return;
      if (response.ok) {
        const data = await response.json();
        if (seq === sceneImagesSeqRef.current) setSceneImages(data);
      } else {
        setSceneImages([]);
      }
    } catch (error) {
      console.error('Error fetching scene images:', error);
      if (seq === sceneImagesSeqRef.current) setSceneImages([]);
    }
  };


  const fetchSiteruns = async (siteCode) => {
    if (!siteCode) return;
    const seq = ++siterunsSeqRef.current;

    setLoadingTimestamps(true);
    try {
      const user = getAuth().currentUser;
      const token = user && await user.getIdToken();

      const response = await fetch(`${BASE_URL}/api/lake-siteruns/${siteCode}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (seq !== siterunsSeqRef.current) return;

      if (response.ok) {
        const data = await response.json();

        if (seq !== siterunsSeqRef.current) return;

        const formattedTimestamps = data
          .filter(item => item.timestamp) // Filter out items without timestamps
          .map(item => {
            const date = new Date(item.timestamp);
            // Check if date is valid
            const isValidDate = !isNaN(date.getTime());
            return {
              label: isValidDate ? date.toLocaleDateString('en-CA') : `Run ${item.run_id?.slice(0, 8) || 'Unknown'}`,
              value: item.run_id
            };
          });

        setAvailableTimestamps(formattedTimestamps);

        if (formattedTimestamps.length > 0) {
          const firstRunId = formattedTimestamps[0].value;

          // ✅ Set run_id — the [selectedSiteCode, selectedRunId] map effect and
          // the [selectedRunId] stats effect pick this up
          setSelectedRunId(firstRunId);
        } else {
          // Clear stats if no runs available
          setStats({
            totalAreaAcre: 0,
            totalWaterAcre: 0,
            infestedPercentage: 0,
            lastUpdated: new Date().toISOString(),
          });
        }

        console.log(`📅 Fetched timestamps for site ${siteCode}`, formattedTimestamps);
      } else {
        console.error('Failed to fetch timestamps');
        setAvailableTimestamps([]);
      }
    } catch (error) {
      console.error('Error fetching timestamps:', error);
      if (seq === siterunsSeqRef.current) {
        setAvailableTimestamps([]);
      }
    } finally {
      if (seq === siterunsSeqRef.current) {
        setLoadingTimestamps(false);
      }
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);




  // // Map initialization
  useEffect(() => {
    if (!selectedOrgId) return;
    const fetchSites = async () => {
      const auth = getAuth();
      const user = auth.currentUser;

      if (user) {
        const idToken = await user.getIdToken();

        try {
          const response = await axios.get(`${BASE_URL}/api/selectionsites?organisation_id=${selectedOrgId}`, {
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          });

          const rawSites = response.data;

          setSites(rawSites);

        } catch (error) {
          console.error("Error fetching sites:", error);
        }
      }
    };

    fetchSites();
  }, [selectedOrgId]);



  useEffect(() => {
    // Filter sites with 'LM' in the site_code
    const lmSites = sites.filter(site => site.site_id.includes("LM"));

    if (lmSites.length > 0 && !selectedSite) {
      const firstSite = lmSites[0];
      console.log("Setting default site:", firstSite.site_name, firstSite.site_id);
      setSelectedSite(firstSite.site_name);
      setSelectedSiteCode(firstSite.site_id.toString());

      // Immediately update map center if polygon data is available
      if (firstSite.polygon) {
        try {
          const geoJson = wellknown.parse(firstSite.polygon);
          let center = null;

          if (geoJson && geoJson.type === "Polygon" && geoJson.coordinates) {
            center = getPolygonCenter(geoJson.coordinates[0]);
          } else if (geoJson && geoJson.type === "MultiPolygon" && geoJson.coordinates?.length > 0) {
            center = getPolygonCenter(geoJson.coordinates[0][0]); // First polygon's outer ring
          }

          if (center) {
            setMapCenter(center);
          } else {
            console.warn("⚠️ Could not compute center from geometry:", geoJson);
          }

          if (geoJson) {
            setSitePolygon([{
              type: "Feature",
              geometry: geoJson,
              properties: {
                site_id: firstSite.site_id,
                name: firstSite.site_name,
                site_number: firstSite.site_number,
              }
            }]);
          }
        } catch (error) {
          console.error("Error parsing polygon data:", error);
        }
      }
    }
  }, [sites]); // Only depend on sites, not selectedSite

  // Deep-link support: preselect site from ?site= URL param, once only.
  const appliedSiteParamRef = useRef(false);
  useEffect(() => {
    if (appliedSiteParamRef.current) return;
    if (sites.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const siteParam = params.get('site');
    if (!siteParam) return;
    const match = sites.find(s => s.site_id === siteParam);
    if (match) {
      appliedSiteParamRef.current = true;
      setSelectedSite(match.site_name);
      handleSiteChange({ target: { value: match.site_name } });
    }
  }, [sites]);


  const [workOrderStats, setWorkOrderStats] = useState({ active: 0, completed: 0, cancelled: 0 });

  const fetchWorkOrders = useCallback(async () => {
    if (!selectedOrgId) {
      console.warn("Organisation ID not set yet");
      return;
    }
    setLoadingWorkOrders(true);
    try {
      const user = getAuth().currentUser;
      const token = user && await user.getIdToken();

      const response = await fetch(
        `${BASE_URL}/api/eco-assets/work-orders?organisation_id=${selectedOrgId}&asset_type=Lake`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setWorkOrders(Array.isArray(data.work_orders) ? data.work_orders : []);
        if (data.stats) setWorkOrderStats(data.stats);
      } else {
        console.error('Failed to load work orders.');
        setWorkOrders([]);
      }
    } catch (error) {
      console.error('Error loading work orders:', error);
      setWorkOrders([]);
    } finally {
      setLoadingWorkOrders(false);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    if (selectedOrgId) fetchWorkOrders();
  }, [selectedOrgId, fetchWorkOrders]);


  // Handle site selection change
  const handleSiteChange = (e) => {
    const siteName = e.target.value;
    setSelectedSite(siteName);

    const selectedSiteObj = sites.find(site => site.site_name === siteName);

    if (selectedSiteObj) {
      console.log("Selected Site:", selectedSiteObj);
      setSelectedSiteCode(selectedSiteObj.site_id.toString());

      if (selectedSiteObj.polygon) {
        console.log("Raw WKT Polygon:", selectedSiteObj.polygon);

        const geoJson = wellknown.parse(selectedSiteObj.polygon);
        console.log("Parsed GeoJSON:", geoJson);

        let center = null;

        if (geoJson && geoJson.type === "Polygon" && geoJson.coordinates) {
          center = getPolygonCenter(geoJson.coordinates[0]);
        } else if (geoJson && geoJson.type === "MultiPolygon" && geoJson.coordinates.length > 0) {
          center = getPolygonCenter(geoJson.coordinates[0][0]); // First polygon's outer ring
        } else {
          console.error("❌ Unsupported geometry type or invalid coordinates:", geoJson);
        }

        if (center && !isNaN(center[0]) && !isNaN(center[1])) {
          console.log("Polygon Center:", center);
          setMapCenter(center);
        } else {
          console.warn("⚠️ Could not compute center from geometry:", geoJson);
        }

        if (geoJson) {
          setSitePolygon([{
            type: "Feature",
            geometry: geoJson,
            properties: {
              site_id: selectedSiteObj.site_id,
              name: selectedSiteObj.site_name,
              site_number: selectedSiteObj.site_number,
            }
          }]);
        }
      } else {
        console.error("Polygon data is missing for the selected site.");
        setSitePolygon([]);
      }
    } else {
      console.log("Site not found!");
      setSelectedSiteCode("");
    }
  };

  const getPolygonCenter = (coordinates) => {
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      console.error("Invalid coordinates for polygon center:", coordinates);
      return null;
    }

    let sumLat = 0, sumLng = 0;
    coordinates.forEach(coord => {
      sumLng += coord[0];
      sumLat += coord[1];
    });

    const center = [sumLng / coordinates.length, sumLat / coordinates.length];
    return isNaN(center[0]) || isNaN(center[1]) ? null : center;
  };


  // Initialize Map
  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: {
          version: 8,
          sources: {
            'google-satellite': {
              type: 'raster',
              tiles: ['https://mt0.google.com/vt/lyrs=s&hl=en&x={x}&y={y}&z={z}'],
              tileSize: 256,
              attribution: 'Google'
            }
          },
          layers: [
            {
              id: 'google-satellite-layer',
              type: 'raster',
              source: 'google-satellite',
              minzoom: 0,
              maxzoom: 22
            }
          ]
        },
        center: mapCenter,
        zoom: 16,
      });

      mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-left");
    }

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  // Modern color palette (Material Design)
  const colors = {
    primary: '#6C8EBF',
    primaryDark: '#47638F',
    success: '#6BA368',
    warning: '#E0A458',
    error: '#D2726A',
    info: '#86A9C4',
    infoDark: '#5E86A3',
    white: '#ffffff',
    grey: '#9e9e9e',
    greyLight: '#e0e0e0',
  };

  const lightred = colors.info;
  const darkred = colors.infoDark;
  const blue = colors.primary;
  const white = colors.white;
  const gray = colors.grey;


  useEffect(() => {
    const map = mapRef.current;
    if (!map || sitePolygon.length === 0) return;

    const setup = () => {
      // Clean up previous site polygon layers
      // Proper cleanup order
      if (map.getLayer("site-boundary-outline")) map.removeLayer("site-boundary-outline");
      if (map.getLayer("site-boundary-layer")) map.removeLayer("site-boundary-layer");
      if (map.getSource("site-boundary")) map.removeSource("site-boundary");


      // Add source
      map.addSource("site-boundary", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: sitePolygon,
        },
      });

      // Add layer
      map.addLayer({
        id: "site-boundary-layer",
        type: "fill",
        source: "site-boundary",
        paint: {
          "fill-color": lightred,
          "fill-opacity": 0,
          "fill-outline-color": darkred,
        },
      });

      // Add outline (border) layer
      map.addLayer({
        id: "site-boundary-outline",
        type: "line",
        source: "site-boundary",
        paint: {
          "line-color": darkred,
          "line-width": 2,
        },
      });
    };

    if (map.isStyleLoaded()) {
      setup();
    } else {
      map.once('idle', setup);
    }

    return () => {
      if (!mapRef.current) return; // map already removed on unmount
      map.off('idle', setup);
    };
  }, [sitePolygon]);

  // Add function to update map with lake-specific MVT data
  const updateMapWithLakeData = (siteCode, runId) => {
    if (!mapRef.current || !siteCode) return;

    const map = mapRef.current;

    // Remove existing lake source and layer if they exist
    if (map.getLayer("lake-infestation-layer")) {
      map.removeLayer("lake-infestation-layer");
    }
    if (map.getSource("lakes")) {
      map.removeSource("lakes");
    }

    let tileUrl = `${BASE_URL}/mvt/lakes/site/${siteCode}/{z}/{x}/{y}.pbf`;
    const urlParams = [];

    if (runId) {
      urlParams.push(`run_id=${runId}`);
    }

    urlParams.push(`_t=${Date.now()}`); // Cache busting

    if (urlParams.length > 0) {
      tileUrl += `?${urlParams.join('&')}`;
    }

    console.log(`🗺️ Adding lake MVT source: ${tileUrl}`);

    map.addSource("lakes", {
      type: "vector",
      tiles: [tileUrl],
      minzoom: 0,
      maxzoom: 22
    });

    map.addLayer({
      id: "lake-infestation-layer",
      type: "fill",
      source: "lakes",
      "source-layer": "lakes",
      paint: {
        "fill-color": "#ff6b6b",
        "fill-opacity": vegVisible ? vegOpacity : 0,
        "fill-outline-color": "#d63031"
      }
    });
  };

  // Add effect to handle map updates when selectedSiteCode changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedSiteCode) return;

    const setup = () => {
      updateMapWithLakeData(selectedSiteCode, selectedRunId);
    };

    if (map.isStyleLoaded()) {
      setup();
    } else {
      map.once('idle', setup);
    }

    return () => {
      if (!mapRef.current) return; // map already removed on unmount
      map.off('idle', setup);
    };
  }, [selectedSiteCode, selectedRunId]);

  // Satellite RGB image raster layer (geo-python download, proxied via
  // /api/lake/scene-tiles). Shown below the vegetation fill layer so
  // vegetation stays visible on top.
  const updateSatelliteImageLayer = async (s3Uri) => {
    const seq = ++satLayerSeqRef.current;
    const map = mapRef.current;
    if (!map) return;

    if (map.getLayer("satellite-image-layer")) map.removeLayer("satellite-image-layer");
    if (map.getSource("satellite-image")) map.removeSource("satellite-image");

    if (!s3Uri) return;

    // Mapbox GL tile URLs cannot send the Authorization header, so the
    // /api/lake/scene-tiles proxy authenticates via a ?token= query param.
    const user = getAuth().currentUser;
    const token = user && await user.getIdToken();
    if (seq !== satLayerSeqRef.current) return;
    if (!token) return;

    // Fetch the raster's footprint (TileJSON bounds/maxzoom) so Mapbox only
    // requests tiles that intersect the COG — tiles outside it are rejected
    // by the tile server with TileOutsideBounds errors. minzoom stays 0:
    // below a raster source's minzoom the layer is hidden entirely, and
    // bounds already limits low-zoom requests to the footprint.
    let bounds = null;
    let maxzoom = 22;
    try {
      console.log(`🛰️ Fetching scene TileJSON for ${s3Uri}`);
      const tjResponse = await fetch(
        `${BASE_URL}/api/lake/scene-tilejson?s3_uri=${encodeURIComponent(s3Uri)}&rescale=0,3000`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (tjResponse.ok) {
        const tj = await tjResponse.json();
        console.log('🛰️ Scene TileJSON:', tj);
        if (Array.isArray(tj.bounds) && tj.bounds.length === 4) bounds = tj.bounds;
        if (Number.isFinite(tj.maxzoom)) maxzoom = tj.maxzoom;
      } else {
        console.warn(`🛰️ Scene TileJSON request failed (HTTP ${tjResponse.status}), continuing without bounds`);
      }
    } catch (error) {
      console.error('Error fetching scene tilejson (continuing without bounds):', error);
    }

    if (seq !== satLayerSeqRef.current) return;

    const tileUrl = `${BASE_URL}/api/lake/scene-tiles/{z}/{x}/{y}.png?s3_uri=${encodeURIComponent(s3Uri)}&rescale=0,3000&token=${encodeURIComponent(token)}`;

    console.log(`🛰️ Adding satellite image source: bounds=${JSON.stringify(bounds)} maxzoom=${maxzoom}`);

    map.addSource("satellite-image", {
      type: "raster",
      tiles: [tileUrl],
      tileSize: 256,
      minzoom: 0,
      maxzoom,
      ...(bounds ? { bounds } : {})
    });

    map.addLayer(
      {
        id: "satellite-image-layer",
        type: "raster",
        source: "satellite-image",
        paint: {
          "raster-opacity": imageVisible ? imageOpacity : 0
        }
      },
      map.getLayer("lake-infestation-layer") ? "lake-infestation-layer" : undefined
    );
  };

  // Look up the scene_images row whose scene_date matches the selected run's
  // date, and (re)build the satellite raster layer for it.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const applyImageLayer = () => {
      const matchingTimestamp = availableTimestamps.find((option) => option.value === selectedRunId);
      const runDate = matchingTimestamp?.label; // formatted 'en-CA' (YYYY-MM-DD)

      const matchingImage = runDate
        ? sceneImages.find((image) => image.scene_date && image.scene_date.slice(0, 10) === runDate)
        : null;

      updateSatelliteImageLayer(matchingImage?.s3_uri);
    };

    // isStyleLoaded() is false during any style mutation — including the
    // "lakes" MVT effect above re-adding its layer in this same commit — so
    // don't bail permanently; retry once the map goes idle.
    if (map.isStyleLoaded()) {
      applyImageLayer();
      return undefined;
    }

    map.once('idle', applyImageLayer);
    return () => {
      if (!mapRef.current) return; // map already removed on unmount
      map.off('idle', applyImageLayer);
    };
  }, [selectedRunId, availableTimestamps, sceneImages]);

  // Vegetation overlay toggle + opacity slider
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer("lake-infestation-layer")) return;
    map.setPaintProperty("lake-infestation-layer", "fill-opacity", vegVisible ? vegOpacity : 0);
  }, [vegVisible, vegOpacity]);

  // Satellite image overlay toggle + opacity slider
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer("satellite-image-layer")) return;
    map.setPaintProperty("satellite-image-layer", "raster-opacity", imageVisible ? imageOpacity : 0);
  }, [imageVisible, imageOpacity]);

  useEffect(() => {
    if (selectedSiteCode && selectedRunId) {
      fetchStats(selectedSiteCode, selectedRunId);
    }
  }, [selectedRunId]);

  useEffect(() => {
    if (mapRef.current && mapCenter) {
      console.log("Moving map to:", mapCenter);
      mapRef.current.flyTo({
        center: mapCenter,
        zoom: 16,
        duration: 1000,
        essential: true
      });
    }
  }, [mapCenter]);


  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    return date.toLocaleString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true // Enables AM/PM format
    })
      .replace(",", "")   // Remove the comma between date and time
      .replace(/\//g, "-"); // Replace slashes with dashes
    // .replace(":", "-");  // Replace colon in time with a dash
  };





  // // Load stats on page load or when selectedSiteCode/selectedRunID changes
  const fetchStats = async (siteCodeParam = selectedSiteCode, runIdParam = selectedRunId) => {
    setLoading(true);
    const seq = ++statsSeqRef.current;

    if (!siteCodeParam) {
      console.log("No site code selected, skipping stats fetch");
      return;
    }

    try {
      const user = getAuth().currentUser;
      const token = user && await user.getIdToken();

      let url = `${BASE_URL}/api/waterstats`;
      const params = new URLSearchParams();

      params.append('site_id', siteCodeParam);

      if (runIdParam) {
        params.append('run_id', runIdParam);
      }

      url += `?${params.toString()}`;
      console.log("Fetching stats from:", url);

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (seq === statsSeqRef.current) {
        setStats({
          totalAreaAcre: data.totalAreaAcre || 0,
          totalWaterAcre: data.totalWaterAcre || 0,
          infestedPercentage: data.infestedPercentage || 0,
          lastUpdated: data.lastUpdated || new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      if (seq === statsSeqRef.current) {
        setLoading(false);
      }
    }
  };





  // // Render Work Orders
  const renderWorkOrders = () => {
    if (!Array.isArray(workOrders)) return null;

    return workOrders.map(order => {
      const stage = order.currentStage;
      const completed = order.stagesCompleted ?? 0;
      const total = order.stagesTotal ?? 0;
      const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;
      return (
        <div
          className="work-order-card"
          key={order.id}
          onClick={(e) => handleRowClick(order, e, "workorder")}
          style={{ cursor: "pointer" }}
        >
          <div className="work-order-header">
            <div className="status">
              <span>Status</span>
              <span className={`Status-badge ${order.status.toLowerCase()}`}>{order.status}</span>
            </div>
            {order.workflowName && (
              <span style={{ fontSize: '0.72rem', color: '#6b7280', marginLeft: 'auto' }}>
                {order.workflowName}
              </span>
            )}
          </div>
          {stage && (
            <div className="detail-row" style={{ marginBottom: '4px' }}>
              <span className="detail-label">Current Stage</span>
              <span style={{ fontWeight: 500, color: '#2563eb' }}>: {stage.name}</span>
            </div>
          )}
          {total > 0 && (
            <div style={{ marginBottom: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.70rem', color: '#6b7280', marginBottom: '2px' }}>
                <span>Progress</span>
                <span>{completed}/{total} stages</span>
              </div>
              <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progressPct}%`, background: progressPct === 100 ? '#10b981' : '#3b82f6', borderRadius: '2px', transition: 'width 0.3s' }} />
              </div>
            </div>
          )}
          <div className="work-order-details">
            <div className="detail-row">
              <span className="detail-label">Title</span>
              <span>: {order.title || order.description || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Assigned To</span>
              <span>: {order.assignedUserName || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Date</span>
              <span>: {formatDate(order.createdAt)}</span>
            </div>
          </div>
        </div>
      );
    });
  };



  // ✅ Load from localStorage initially
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved ? JSON.parse(saved) : false;
  });

  // ✅ Save to localStorage when changed
  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
  };



  return (
    <Fragment>
      <div className="public-sans">
          {/* Main Content */}
          <main>

            {/* Map Container - Fills entire background */}
            <div 
              id="map" 
              ref={mapContainerRef} 
              className="map-container"
            ></div>
            {/* ✅ Expandable Floating Panels - Fixed: Added z-index */}
            <div 
              className={`SiteBarParentSection ${isCollapsed ? 'expand' : 'shrink'} ${isPanelMinimized ? 'minimized' : ''}`}
              style={{ zIndex: 'var(--z-panels)' }}
            >
              <div className="resizable-div SiteBarSubSection">
                {isPanelMinimized ? (
                  <div className="SiteSelectionDiv">
                    <div className="SiteLabelMinimizeDiv">
                      <span className="SiteLabelText">Lake Details</span>
                      <span
                        className="SiteLabelIcon"
                        onClick={() => setIsPanelMinimized(false)}
                        style={{ cursor: 'pointer' }}
                      >
                        {/* Expand Icon */}
                        <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24"
                          strokeLinecap="round" strokeLinejoin="round" height="1em" width="1em"
                          xmlns="http://www.w3.org/2000/svg">
                          <title>Expand</title>
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M20 6v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2z" />
                          <path d="M20 9h-16" />
                          <path d="M10 14l2 2l2 -2" />
                        </svg>
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Full Panel Content */}
                    <div className="SiteSelectionDiv">
                      <div className="SiteLabelMinimizeDiv">
                        <span className="SiteLabelText">Lake Details</span>
                        <span className="SiteLabelIcon" onClick={() => setIsPanelMinimized((prev) => !prev)} style={{ cursor: 'pointer' }}>
                          <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                            <title>Minimize</title>
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M20 6v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2z"></path>
                            <path d="M20 15h-16"></path>
                            <path d="M14 10l-2 -2l-2 2"></path>
                          </svg>
                        </span>
                      </div>
                      <div className="SiteSelectionSelectDiv">
                        <Box sx={{ minWidth: 170 }}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel
                              id="site-select-label"
                              sx={{
                                fontSize: '12px',
                                fontFamily: "'Public Sans', sans-serif",
                                top: '-8px', // same as Timestamp
                                '&.MuiInputLabel-shrink': {
                                  top: '0px', // floating position
                                },
                              }}>Site</InputLabel>
                            <Select
                              labelId="site-select-label"
                              id="site-select"
                              value={selectedSite}
                              label="Site"
                              onChange={handleSiteChange}
                              sx={{
                                fontSize: '12px',
                                fontFamily: "'Public Sans', sans-serif",
                                height: '35px',
                                borderRadius: '8px',
                                backgroundColor: white,
                                boxShadow: 'none',
                                '.MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'border',
                                  borderWidth: '1px',
                                  borderStyle: 'solid'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: gray },
                                '.MuiSelect-select': {
                                  paddingTop: '10px',
                                  paddingBottom: '10px',
                                },
                              }}
                            >
                              <MenuItem value="">Select a Site</MenuItem>
                              {sites
                                .filter((site) => site.site_id.includes("LM")) // filter same as before
                                .map((site) => (
                                  <MenuItem key={site.site_id} value={site.site_name}>
                                    {site.site_name}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </Box>
                        <header>
                          <Button
                            variant="contained"
                            className="ShowBtnStyle"
                            onClick={() => console.log("Selected Site:", selectedSite, "Code:", selectedSiteCode)}
                            sx={{
                              backgroundColor: blue,  // example blue
                              color: white,
                              fontSize: "12px",            // 👈 text size here
                              padding: "6px 16px",
                              borderRadius: "6px",
                              textTransform: "none", // prevent ALL CAPS
                              height: "30px",
                              width: "70px",
                              lineHeight: "1px",
                              fontFamily: "'Public Sans', sans-serif",
                              "&:hover": {
                                backgroundColor: blue,
                              }
                            }}
                          >
                            Show
                          </Button>
                        </header>

                        {/* <ShowButton className="ShowBtnStyle" onClick={() => console.log("Selected Site:", selectedSite, "Code:", selectedSiteCode)} /> */}
                      </div>

                      {/* Display selected site & code */}
                      {selectedSite && (
                        <div className="SiteSelectionDiv">
                          <span className="SiteLabelText">{selectedSite}</span>
                          <div>
                            <span className="SiteCodeDiv">Site Code : {selectedSiteCode ? selectedSiteCode : "N/A"}</span>
                          </div>
                        </div>
                      )}

                      {/* V7 Site Operations card (Feature 4) */}
                      {selectedSiteCode && <SiteOpsCard siteId={selectedSiteCode} />}

                      <div className="SiteSelectionDiv">
                        <div className="SiteLabelMinimizeDiv">
                          <span className="SiteLabelText">Timestamp Filter</span>
                        </div>
                        {/* <select
                          className="SiteSelectionSelect"
                          value={selectedRunId}
                          onChange={(e) => setSelectedRunId(e.target.value)}
                        >
                          {availableTimestamps.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select> */}

                        <Box sx={{ minWidth: 240 }}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel
                              id="timestamp-select-label"
                              sx={{
                                fontSize: '12px',
                                fontFamily: "'Public Sans', sans-serif",
                                top: '-8px',
                                '&.MuiInputLabel-shrink': {
                                  top: '0px',
                                },
                              }}
                            >
                            </InputLabel>
                            <Select
                              labelId="timestamp-select-label"
                              id="timestamp-select"
                              value={selectedRunId}
                              onChange={(e) => setSelectedRunId(e.target.value)}
                              label=""
                              sx={{
                                fontSize: '12px',
                                fontFamily: "'Public Sans', sans-serif",
                                height: '35px',
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                boxShadow: 'none',
                                '.MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'border',
                                  borderWidth: '1px',
                                  borderStyle: 'solid'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'gray' },
                                '.MuiSelect-select': {
                                  paddingTop: '10px',
                                  paddingBottom: '10px',
                                },
                              }}
                            >
                              {availableTimestamps.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </div>

                      {selectedSiteCode && (
                        <div className="SiteSelectionDiv-siterun">
                          <div className="SiteLabelMinimizeDiv">
                            <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={vegVisible}
                                onChange={(e) => setVegVisible(e.target.checked)}
                              />
                              Vegetation Overlay
                            </label>
                          </div>
                          <div className="SiteSelectionSelectDiv">
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.1"
                              value={vegOpacity}
                              disabled={!vegVisible}
                              onChange={(e) => setVegOpacity(parseFloat(e.target.value))}
                              style={{ width: '100%' }}
                            />
                            <span style={{ fontSize: '12px', marginLeft: '10px' }}>
                              {Math.round(vegOpacity * 100)}%
                            </span>
                          </div>
                        </div>
                      )}

                      {selectedSiteCode && (
                        <div className="SiteSelectionDiv-siterun">
                          <div className="SiteLabelMinimizeDiv">
                            <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={imageVisible}
                                onChange={(e) => setImageVisible(e.target.checked)}
                              />
                              Satellite Image Overlay
                            </label>
                          </div>
                          <div className="SiteSelectionSelectDiv">
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.1"
                              value={imageOpacity}
                              disabled={!imageVisible}
                              onChange={(e) => setImageOpacity(parseFloat(e.target.value))}
                              style={{ width: '100%' }}
                            />
                            <span style={{ fontSize: '12px', marginLeft: '10px' }}>
                              {Math.round(imageOpacity * 100)}%
                            </span>
                          </div>
                        </div>
                      )}

                      <Box sx={getTreeDetailsParentSx(theme)}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box sx={getTreeHeadingDivSx(theme)}>
                            <Box component="span" sx={getTreesTextSx(theme)}>Water Bodies</Box>
                          </Box>

                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box sx={getStatCardEcologicalSx(theme)}>
                              <Box sx={getStatIconWrapperSx(theme)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="27" height="36" viewBox="0 0 27 36" fill="none">
                                  <path d="M13.5 36C9.84095 35.9135 6.36569 34.3918 3.83575 31.7685C1.30581 29.1451 -0.0724652 25.634 0.00293875 22.0045C0.00306687 19.0051 0.940574 16.0795 2.6864 13.6304L9.72082 3.86275L11.7172 5.2769L4.68151 15.0445C3.23378 17.0813 2.45674 19.5125 2.45695 22.0045C2.38135 24.9885 3.50103 27.8807 5.57081 30.0476C7.64057 32.2146 10.4918 33.4797 13.5 33.566C16.5082 33.4797 19.3594 32.2146 21.4292 30.0476C23.499 27.8807 24.6186 24.9885 24.5431 22.0045C24.5433 19.5125 23.7662 17.0813 22.3185 15.0445L12.5024 1.41415L14.4976 0L24.3136 13.6304C26.0594 16.0795 26.9969 19.0051 26.9971 22.0045C27.0725 25.634 25.6942 29.1451 23.1643 31.7685C20.6343 34.3918 17.1591 35.9135 13.5 36Z" fill="currentColor"></path>
                                  <path d="M18.6793 14.3487L14.7528 8.50708C14.6341 8.34967 14.48 8.22188 14.3028 8.13386C14.1256 8.04583 13.9302 8 13.732 8C13.5338 8 13.3383 8.04583 13.1611 8.13386C12.9839 8.22188 12.8298 8.34967 12.7111 8.50708L8.78468 14.3487C8.0021 15.5234 7.58872 16.902 7.59694 18.31C7.55062 19.7917 8.01009 21.2455 8.90089 22.4359C9.7917 23.6262 11.0616 24.4834 12.505 24.8684V28.6545H14.959V18.9185H12.505V22.3078C11.7471 21.9519 11.1108 21.3836 10.6747 20.6732C10.2386 19.9628 10.0218 19.1412 10.051 18.31C10.0475 17.3831 10.3171 16.4754 10.8264 15.6983L13.732 11.3731L16.6375 15.6947C17.1476 16.4728 17.4171 17.3819 17.413 18.31C17.4223 19.3338 17.0911 20.3322 16.4706 21.1505L18.4142 22.6376C19.31 21.4497 19.8158 20.0173 19.8632 18.5346C19.9106 17.0519 19.4973 15.5906 18.6793 14.3487Z" fill="currentColor"></path>
                                </svg>
                              </Box>
                              <Box sx={getStatInfoDivSx(theme)}>
                                <Box component="span" sx={getStatLabelSx(theme)}>Lake Area</Box>
                                <Box component="span" sx={getStatValueSx(theme)}>{loading ? <Skeleton width={60} /> : stats.totalWaterAcre}</Box>
                              </Box>
                            </Box>

                            <Box sx={getStatCardEcologicalSx(theme)}>
                              <Box sx={getStatInfoDivSx(theme)} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                <Box component="span" sx={getStatLabelSx(theme)}>Infested Area</Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 17" fill="none" style={{ width: 16.5, height: 16.5 }}>
                                    <path d="M13.5807 1.375H5.07727C4.97781 1.375 4.88243 1.41451 4.81211 1.48483C4.74178 1.55516 4.70227 1.65054 4.70227 1.75C4.70227 1.84946 4.74178 1.94484 4.81211 2.01517C4.88243 2.08549 4.97781 2.125 5.07727 2.125H13.5807C13.7912 2.12521 13.9931 2.20893 14.142 2.3578C14.2909 2.50667 14.3746 2.70852 14.3749 2.91907V11.4227C14.3749 11.5222 14.4144 11.6176 14.4847 11.6879C14.555 11.7582 14.6504 11.7977 14.7499 11.7977C14.8493 11.7977 14.9447 11.7582 15.015 11.6879C15.0854 11.6176 15.1249 11.5222 15.1249 11.4227V2.91907C15.1244 2.50967 14.9616 2.11718 14.6721 1.82771C14.3826 1.53823 13.9901 1.37542 13.5807 1.375Z" fill="#9E017C"></path>
                                    <path d="M13.8459 12.6467C13.7744 12.5786 13.6795 12.5405 13.5807 12.5405C13.482 12.5405 13.3871 12.5786 13.3156 12.6467C13.2808 12.6815 13.2532 12.7229 13.2343 12.7684C13.2155 12.8139 13.2057 12.8626 13.2057 12.9119C13.2057 12.9611 13.2155 13.0099 13.2343 13.0554C13.2532 13.1009 13.2808 13.1422 13.3156 13.177L13.8447 13.7059H3.58807C3.37756 13.7057 3.17575 13.6219 3.02691 13.4731C2.87807 13.3242 2.79435 13.1224 2.79413 12.9119V2.65527L3.32294 3.1842C3.39438 3.25237 3.48933 3.2904 3.58807 3.2904C3.68682 3.2904 3.78177 3.25237 3.85321 3.1842C3.88804 3.14939 3.91567 3.10806 3.93452 3.06257C3.95337 3.01707 3.96307 2.96831 3.96307 2.91907C3.96307 2.86982 3.95337 2.82106 3.93452 2.77557C3.91567 2.73007 3.88804 2.68874 3.85321 2.65393L2.68426 1.48486C2.61282 1.41669 2.51787 1.37866 2.41913 1.37866C2.32038 1.37866 2.22543 1.41669 2.15399 1.48486L0.984802 2.65393C0.914568 2.72429 0.875122 2.81965 0.875122 2.91907C0.875122 3.01848 0.914568 3.11384 0.984802 3.1842C1.05624 3.25237 1.15119 3.2904 1.24994 3.2904C1.34868 3.2904 1.44363 3.25237 1.51507 3.1842L2.04413 2.65527V12.9119C2.04457 13.3212 2.20738 13.7137 2.49682 14.0031C2.78627 14.2926 3.17872 14.4555 3.58807 14.4559H13.8447L13.3156 14.9849C13.2808 15.0197 13.2532 15.061 13.2343 15.1065C13.2155 15.152 13.2058 15.2008 13.2058 15.25C13.2058 15.2992 13.2155 15.348 13.2343 15.3935C13.2532 15.439 13.2808 15.4803 13.3156 15.5151C13.3504 15.55 13.3918 15.5776 13.4373 15.5964C13.4827 15.6153 13.5315 15.625 13.5807 15.625C13.63 15.625 13.6787 15.6153 13.7242 15.5964C13.7697 15.5776 13.8111 15.55 13.8459 15.5151L15.0151 14.3461C15.0499 14.3113 15.0775 14.2699 15.0964 14.2244C15.1152 14.1789 15.1249 14.1302 15.1249 14.0809C15.1249 14.0317 15.1152 13.9829 15.0964 13.9374C15.0775 13.8919 15.0499 13.8506 15.0151 13.8158L13.8459 12.6467Z" fill="#9E017C"></path>
                                  </svg>
                                  <Box component="span" sx={getStatValueSx(theme)}>{loading ? <Skeleton width={60} /> : stats.totalAreaAcre}</Box>
                                </Box>
                              </Box>
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={getStatCardEcologicalSx(theme)}>
                                <Box sx={getStatInfoDivSx(theme)} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                  <Box component="span" sx={getStatLabelSx(theme)}>Infested Percentage</Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="19.5" height="19.5" viewBox="0 0 24 24" fill="none">
                                      <path fill="#9E017C" d="M12 2a1 1 0 0 1 1 1v1.05a7.978 7.978 0 0 1 3.663 1.519l.744-.744a1 1 0 1 1 1.414 1.414l-.744.744A7.978 7.978 0 0 1 20.95 11H22a1 1 0 1 1 0 2h-1.05a7.978 7.978 0 0 1-1.519 3.663l.744.744a1 1 0 0 1-1.414 1.414l-.744-.744A7.978 7.978 0 0 1 13 20.95V22a1 1 0 1 1-2 0v-1.05a7.978 7.978 0 0 1-3.663-1.519l-.744.744a1 1 0 1 1-1.414-1.414l.744-.744A7.978 7.978 0 0 1 3.05 13H2a1 1 0 1 1 0-2h1.05a7.978 7.978 0 0 1 1.519-3.663l-.744-.744a1 1 0 1 1 1.414-1.414l.744.744A7.978 7.978 0 0 1 11 3.05V2a1 1 0 0 1 1-1zm0 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10z" />
                                    </svg>
                                    <Box component="span" sx={getStatValueSx(theme)}>
                                      {loading ? <Skeleton width={60} /> : stats.infestedPercentage ?? 'N/A'}
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                              
                              <Box sx={getStatCardEcologicalSx(theme)}>
                                <Box sx={getStatInfoDivSx(theme)} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                  <Box component="span" sx={getStatLabelSx(theme)}>Last Updated On</Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16.5" height="16.5" fill="none" viewBox="0 0 24 24">
                                      <path fill="#9E017C" d="M7 2a1 1 0 1 0 0 2h10a1 1 0 1 0 0-2H7zM3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7zm2 0v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7H5z" />
                                    </svg>
                                    <Box component="span" sx={getStatValueSx(theme)}>{loading ? <Skeleton width={60} /> : stats.lastUpdated || "N/A"}</Box>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              {/* <Button variant="contained" sx={{ alignSelf: 'center', mt: 2, fontSize: '10px', padding: '4px 10px' }} onClick={handleOpen}>
                                Show History
                              </Button> */}
                              <Button
                                variant="contained"
                                onClick={handleOpen}
                                sx={{
                                  backgroundColor: blue,
                                  alignSelf: 'center', mt: 3, // example blue
                                  color: white,
                                  fontSize: "11px",            // 👈 text size here
                                  padding: "6px 16px",
                                  borderRadius: "6px",
                                  textTransform: "none", // prevent ALL CAPS
                                  height: "30px",
                                  lineHeight: "1px",
                                  fontFamily: "'Public Sans', sans-serif",
                                  "&:hover": {
                                    backgroundColor: blue,
                                  }
                                }}
                              >
                                Show History
                              </Button>
                            </Box>
                            
                            {/* Modal */}
                            <Modal open={open} onClose={handleClose} aria-labelledby="history-modal-title">
                              <Box sx={{
                                position: 'absolute', top: '50%', left: '50%',
                                transform: 'translate(-50%, -50%)', 
                                width: { xs: '90%', sm: 800, md: 900 },
                                maxHeight: '90vh',
                                bgcolor: 'background.paper', 
                                boxShadow: 24, 
                                p: 3, 
                                borderRadius: 2,
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: 2,
                                overflow: 'auto'
                              }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography id="history-modal-title" variant="h6" sx={{ fontWeight: 600 }}>
                                    Lake Status History
                                  </Typography>
                                  <IconButton onClick={handleClose} sx={{ color: 'gray' }}>
                                    <CloseIcon />
                                  </IconButton>
                                </Box>
                                <Box sx={{ minHeight: 400 }}>
                                  <LakeStatusChart selectedSiteCode={selectedSiteCode} />
                                </Box>
                              </Box>
                            </Modal>
                          </Box>
                        </Box>
                      </Box>

                      <SentinelAnalysisPanel
                        geometryFeature={sitePolygon[0]}
                        selectedSiteCode={selectedSiteCode}
                        selectedOrgId={selectedOrgId}
                        onAnalysisComplete={() => {
                          fetchSiteruns(selectedSiteCode);
                          fetchSceneImages(selectedSiteCode);
                        }}
                      />

                    </div>
                  </>
                )}
              </div>
            </div>
          </main>
          {/* Work Orders Panel - Positioned by CSS with 16px gap */}
          <div 
            className={`WorkOrdBarParentSection ${isPanelMinimized ? 'minimized' : ''}`} 
            style={{ zIndex: 'var(--z-panels)' }}
          >
            <div className="WorkOrdBarSubSection">
              <div className="header">
                <span>Work orders ({(workOrderStats.active ?? workOrderStats.pending ?? 0) + (workOrderStats.completed ?? 0) + (workOrderStats.cancelled ?? 0)})</span>
                {can("Task Management", "taskAssignment", "create") && (
                  <Button
                    variant="contained"
                    id="addWorkOrder"
                    className="AddBtnStyle"
                    onClick={() => setCreateTaskOpen(true)}
                    sx={{
                      backgroundColor: blue,
                      color: white,
                      fontSize: "12px",
                      padding: "6px 16px",
                      borderRadius: "6px",
                      textTransform: "none",
                      height: "30px",
                      width: "70px",
                      lineHeight: "1px",
                      fontFamily: "'Public Sans', sans-serif",
                      "&:hover": {
                        backgroundColor: blue,
                      }
                    }}
                  >
                    Add
                  </Button>
                )}
              </div>

              {!isPanelMinimized && (
                <>
                  <div className="status-filters">
                    <span className="open">Active ({workOrderStats.active ?? workOrderStats.pending ?? 0})</span>
                    <span className="closed">Completed ({workOrderStats.completed ?? 0})</span>
                    <span className="cancelled">Cancelled ({workOrderStats.cancelled ?? 0})</span>
                  </div>
                  <div className="panel-content">
                    <div className="search-container-tree">
                      <input type="text" placeholder="Search work orders" aria-label="Search work orders" />
                    </div>
                    <div className="work-orders-list">
                      {loadingWorkOrders ? (
                        Array.from({ length: 3 }).map((_, idx) => (
                          <div key={idx} style={{ marginBottom: '12px' }}>
                            <Skeleton height={200} borderRadius={8} />
                          </div>
                        ))
                      ) : (
                        renderWorkOrders()
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <UnifiedTaskModal
          open={isTaskModalOpen}
          onClose={handleCloseModal}
          taskId={selectedTaskId}
          onUpdated={fetchWorkOrders}
        />

        {/* Create Task Dialog (replaces inline form) */}
        <CreateTaskDialog
          open={createTaskOpen}
          onClose={() => setCreateTaskOpen(false)}
          onCreated={(newTaskId) => {
            setCreateTaskOpen(false);
            fetchWorkOrders();
            if (newTaskId) {
              setSelectedTaskId(newTaskId);
              setIsTaskModalOpen(true);
            }
          }}
          prefillSiteId={selectedSiteCode}
          restrictAssetTypeId={2}
        />
    </Fragment>
  );
}

export default Lake;